import { Fragment, useEffect, useState } from "react";
import Header from "../../typography/Header/Header";
import Text from "../../typography/Text/Text";
import Button from "../../components/atoms/Button/Button";
import logoHead from "../../images/png/logoHead.png";
import {
  AdjustmentType,
  PatternData,
  PatternHeadPropsWithData,
} from "../../types/types";
import DiscountTag from "../../components/atoms/DiscountTag";
import {
  formatToCurrency,
  getLanguagesForPack,
  translateLanguages,
} from "../../utils/utils";
import Layout from "../../components/Layout/Layout";
import { graphql } from "gatsby";
import { useCurrencyContext } from "../../hooks/useCurrencyContext";
import { Trans, useTranslation } from "gatsby-plugin-react-i18next";
import React from "react";
import useSEO from "../../hooks/useSEO";
import { GatsbyImage, IGatsbyImageData, getImage } from "gatsby-plugin-image";
import * as styles from "./patternDetails.module.css";
import useWindowSize from "../../hooks/useWindowSize";
import { useFetchPatterns } from "../../hooks/useFetchPatterns";
import { useCartContext } from "../../hooks/useCartContext";
import slugify from "slugify";

interface PageContext {
  pattern: PatternData;
}

interface PatternDetailsProps {
  pageContext: PageContext;
}

const patternDetails: React.FC<PatternDetailsProps> = ({
  pageContext: { pattern },
}) => {
  const allPatterns = useFetchPatterns();
  const [languages, setLanguages] = useState("");
  const [displayedImage, setDisplayedImage] = useState<
    IGatsbyImageData | undefined
  >(undefined);
  const [secondaryImages, setSecondaryImages] = useState<IGatsbyImageData[]>(
    []
  );
  const { currency } = useCurrencyContext();
  const { t, i18n } = useTranslation();
  const { width } = useWindowSize();
  const { addToCart, openCart } = useCartContext();
  const mainImageData =
    pattern.localMainImage?.childImageSharp?.gatsbyImageData ||
    getImage(logoHead); // 'logoHead' as fallback

  useEffect(() => {
    if (pattern) {
      const mainImgData =
        pattern.localMainImage?.childImageSharp?.gatsbyImageData;
      setDisplayedImage(mainImgData);

      const combinedImages = [
        ...(mainImgData ? [mainImgData] : []),
        ...(pattern.localSecondaryImages?.map(
          (img) => img.childImageSharp.gatsbyImageData
        ) ?? []),
      ];

      setSecondaryImages(combinedImages);

      let languageList: string[] = [];
      if (pattern.packPatternIds && pattern.packPatternIds.length > 0) {
        languageList = getLanguagesForPack(allPatterns, pattern.packPatternIds);
      } else {
        languageList = pattern.files.map((file) => file.language);
      }
      const formattedLanguages = translateLanguages(
        languageList,
        i18n.language as "en" | "pt"
      );
      setLanguages(formattedLanguages);
    }
  }, [pattern, i18n.language]);

  const handleImageClick = (imageData: IGatsbyImageData) => {
    setDisplayedImage(imageData);
  };

  const handleAddToCart = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const cartItem = {
      id: pattern.id || "",
      name: pattern.patternName.en,
      price: { usd: pattern.price.usd, brl: pattern.price.brl },
      thumbnail: mainImageData,
    };
    addToCart(cartItem);
    openCart();
  };

  if (!pattern) {
    return <div>Receita não encontrada</div>;
  }

  return (
    <Layout>
      <Header As="h1">
        {pattern.patternName[i18n.language as "en" | "pt"]} - Detailed Amigurumi
        Pattern
      </Header>
      <div className={styles.container}>
        {width && width < 750 && (
          <Header
            As="h2"
            color="grey70"
            size="30"
            bold
            className={styles.mobileHeader}
          >
            {pattern.patternName[i18n.language as "en" | "pt"]}
          </Header>
        )}
        <div className={styles.imagesContainer}>
          <div>
            {displayedImage && (
              <GatsbyImage
                image={displayedImage}
                alt={`${
                  pattern.patternName[i18n.language as "en" | "pt"]
                } main image`}
                className={styles.mainImage}
              />
            )}
            {pattern.adjustmentType === AdjustmentType.DiscountPercentage && (
              <DiscountTag pattern={pattern} />
            )}
          </div>
          <div className={styles.secondaryImagesContainer}>
            {secondaryImages.map((imageData, index) => (
              <div key={index} onClick={() => handleImageClick(imageData)}>
                <GatsbyImage
                  alt={`${
                    pattern.patternName[i18n.language as "en" | "pt"]
                  } - ${index}`}
                  image={imageData}
                  className={styles.secondaryImage}
                />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.textContainer}>
          {width && width > 750 && (
            <Header As="h2" color="grey70" size="30" bold>
              {pattern.patternName[i18n.language as "en" | "pt"]}
            </Header>
          )}
          <Text color="grey" className={styles.description}>
            {pattern.description[i18n.language as "en" | "pt"]}
          </Text>
          <div className={styles.subSection}>
            <Header
              As="h3"
              color="grey70"
              size="20"
              bold
              className={styles.subHeader}
            >
              {t("patterndetails.materials")}
            </Header>
            <Text color="grey" className={styles.description}>
              {pattern.materials[i18n.language as "en" | "pt"]}
            </Text>
          </div>
          <div className={styles.subSection}>
            <Header
              As="h3"
              color="grey70"
              size="20"
              bold
              className={styles.subHeader}
            >
              {t("patterndetails.languages")}
            </Header>
            <Text color="grey">
              {t("patterndetails.availableLanguages", { languages: languages })}
            </Text>
          </div>
          <div className={styles.subSection}>
            <Header
              As="h3"
              color="grey70"
              size="20"
              bold
              className={styles.subHeader}
            >
              {t("patterndetails.difficultyLevel")}
            </Header>
            <Text color="grey">{t("patterndetails.intermediateLevel")}</Text>
          </div>
          <div className={styles.ctaSection}>
            <div>
              {pattern.adjustmentType === AdjustmentType.DiscountPercentage && (
                <Text color="light-grey" bold className={styles.initialPrice}>
                  {formatToCurrency(
                    currency === "USD"
                      ? pattern.initialPrice.usd
                      : pattern.initialPrice.brl,
                    currency
                  )}
                </Text>
              )}
              <Text color="blue" className={styles.mainPrice}>
                {formatToCurrency(
                  currency === "USD" ? pattern.price.usd : pattern.price.brl,
                  currency
                )}
              </Text>
            </div>
            <div className={styles.addToCart}>
              <Button round fluid onClick={(event) => handleAddToCart(event)}>
                {t("patterndetails.addToCart")}
              </Button>
            </div>
          </div>
          <div className={styles.subSection}>
            <Header
              As="h3"
              color="grey70"
              size="20"
              bold
              className={styles.subHeader}
            >
              {t("patterndetails.important")}
            </Header>
            <Text color="grey">{t("patterndetails.digitalPatternNotice")}</Text>
            <Text color="grey">
              <Trans
                i18nKey="patterndetails.accessToPatterns"
                components={{ 1: <strong /> }}
              />
            </Text>
            <Text color="grey">
              <Trans
                i18nKey="patterndetails.detailedDescription"
                components={[<Fragment key="0" />, <strong key="1" />]}
                values={{
                  pattern: pattern.patternName[i18n.language as "en" | "pt"],
                }}
              ></Trans>
            </Text>
            <Text color="grey" bold>
              {t("patterndetails.contactUs")}
            </Text>
            <Text color="grey" bold>
              {t("patterndetails.noResell")}
            </Text>
            <Text color="grey" bold>
              {t("patterndetails.shareHashtag")}
            </Text>
          </div>
          <div className={styles.subSection}>
            <Header As="h4" color="grey70" size="16" bold>
              {t("patterndetails.usageRights")}
            </Header>
            <Text color="grey" size="14">
              {t("patterndetails.usageRightsDescription")}
            </Text>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default patternDetails;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

export function Head({
  data,
  location,
  pageContext: { pattern },
}: PatternHeadPropsWithData) {
  const language = data.locales.edges[0].node.language;

  const title = `Crocheniacs | ${pattern.patternName[language]} - ${
    JSON.parse(data.locales.edges[0].node.data).patterndetails.seoTitle
  }`;
  const description = `${pattern.patternName[language]}, ${
    JSON.parse(data.locales.edges[0].node.data).patterndetails.seoDescription
  }`;
  const seo = useSEO({
    title: title,
    description: description,
    // imageUrl: "/about-header.jpg",
    pathname: location.pathname,
  });

  const structuredDataProduct = {
    "@context": "http://schema.org",
    "@type": "Product",
    name: pattern.patternName.en,
    image:
      pattern.localMainImage?.childImageSharp?.gatsbyImageData?.images?.fallback
        ?.src,
    description: pattern.description.en,
    sku: pattern.id,
    mpn: pattern.id,
    brand: {
      "@type": "Brand",
      name: "Crocheniacs",
    },
    category: pattern.category,
    offers: {
      "@type": "Offer",
      url: `https://crocheniacs.com/pattern/${slugify(pattern.patternName.en, {
        lower: true,
        strict: true,
        trim: true,
      })}`,
      priceCurrency: "USD",
      price: pattern.price.usd,
      priceValidUntil: "2024-12-31",
      itemCondition: "http://schema.org/NewCondition",
      availability: "http://schema.org/InStock",
      seller: {
        "@type": "Organization",
        name: "Crocheniacs",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        hasDigitalDelivery: true,
      },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "ALL",
        returnPolicyCategory: "http://schema.org/NoReturns",
      },
    },
  };

  return (
    <>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:url" content={seo.url} />
      <link
        rel="canonical"
        href={`https://crocheniacs.com${location.pathname}`}
      />
      <script type="application/ld+json">
        {JSON.stringify(structuredDataProduct)}
      </script>
    </>
  );
}
